/**
 * React context provider for KeyboardShortcuts instance.
 * Provides a simple way to share a KeyboardShortcuts instance across the component tree.
 */

import { createContext, type ReactNode, useContext } from 'react';

import type { KeyboardShortcuts } from '../keyboard-shortcuts';

/**
 * Context for KeyboardShortcuts instance.
 * null when no provider is present.
 */
export const KeyboardShortcutsContext = createContext<KeyboardShortcuts | null>(null);

/**
 * Props for KeyboardShortcutsProvider component.
 */
export interface KeyboardShortcutsProviderProps {
  /** KeyboardShortcuts instance to provide to children */
  instance: KeyboardShortcuts;
  /** Child components that can access the instance via context */
  children: ReactNode;
}

/**
 * Provider component that shares a KeyboardShortcuts instance via React context.
 * Pass an instance from `useKeyboardShortcutsInstance`, which owns creation
 * and teardown.
 *
 * @example
 * ```tsx
 * const shortcuts = useKeyboardShortcutsInstance({
 *   configure: (instance) => instance.enableScope('command-palette'),
 * });
 *
 * <KeyboardShortcutsProvider instance={shortcuts}>
 *   <App />
 * </KeyboardShortcutsProvider>
 * ```
 */
export function KeyboardShortcutsProvider({ instance, children }: KeyboardShortcutsProviderProps) {
  return <KeyboardShortcutsContext.Provider value={instance}>{children}</KeyboardShortcutsContext.Provider>;
}

/**
 * Hook to access the KeyboardShortcuts instance from context.
 * Must be used within a KeyboardShortcutsProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const shortcuts = useKeyboardShortcuts();
 *
 *   const openModal = () => {
 *     shortcuts.disableScope('global');
 *     shortcuts.enableScope('modal');
 *   };
 * }
 * ```
 *
 * @throws Error if used outside of KeyboardShortcutsProvider
 * @returns The KeyboardShortcuts instance
 */
export function useKeyboardShortcuts(): KeyboardShortcuts {
  const instance = useContext(KeyboardShortcutsContext);
  if (!instance) {
    throw new Error('useKeyboardShortcuts must be used within KeyboardShortcutsProvider');
  }
  return instance;
}
